import { FormattedInputChoice } from 'types/kontentAi';

import {
  Condition,
  ContentCardItem,
  Destination,
  GatedConditions,
} from 'types/content';
import { UserDynamicValues } from 'types/user';

import { findNestedValue } from './objects';

const TRUE_LIST = [true, 'true', 'yes', 'y'];
const FALSE_LIST = [false, 'false', 'n', 'n/a', 'no'];

/**
 * Runs checks against conditions to gate content library content
 *
 * Note as conditions grow so will this function
 *
 *
 * Returns an boolean
 */
export function gatedConditionsCheck(
  conditions: GatedConditions,
  completedEligibility: boolean
): boolean {
  if (
    conditions.some(
      (condition) => condition.codename === 'eligibility_form_required'
    ) &&
    !completedEligibility
  ) {
    return true;
  }

  return false;
}

function checkCondition(
  condition: Condition,
  variableValue: boolean | number | string | string[] | undefined
) {
  const { expectedValue, operator } = condition;
  const sanitizedValue =
    typeof variableValue === 'string'
      ? variableValue.toLowerCase()
      : variableValue;
  switch (operator) {
    case 'Equals':
      if (typeof sanitizedValue === 'number') {
        return Number(sanitizedValue) === Number(expectedValue);
      }
      return sanitizedValue === expectedValue.toLowerCase();
    case 'GreaterThan':
      return Number(sanitizedValue) > Number(expectedValue);
    case 'GreaterThanOrEqual':
      return Number(sanitizedValue) >= Number(expectedValue);
    case 'IsFalse':
      return FALSE_LIST.includes(sanitizedValue as string);
    case 'isIncludedIn':
      return (sanitizedValue as string[]).includes(expectedValue);
    case 'isNotIncludedIn':
      return !(sanitizedValue as string[]).includes(expectedValue);
    case 'isNotSet':
      return sanitizedValue === undefined || sanitizedValue === null;
    case 'isSet':
      return sanitizedValue !== undefined && sanitizedValue !== null;
    case 'IsTrue':
      return TRUE_LIST.includes(sanitizedValue as string);
    case 'LessThan':
      return Number(sanitizedValue) < Number(expectedValue);
    case 'LessThanOrEqual':
      return Number(sanitizedValue) <= Number(expectedValue);
    case 'NotEquals':
      if (typeof sanitizedValue === 'number') {
        return Number(sanitizedValue) !== Number(expectedValue);
      }
      return sanitizedValue !== expectedValue.toLowerCase();
    default:
      return false;
  }
}

export const checkBranchCondition = (
  data: UserDynamicValues,
  condition?: Condition
): boolean => {
  if (condition) {
    const { operator, variableName } = condition;
    const variableValue = findNestedValue(variableName, data);

    if (
      (variableValue === undefined || null) &&
      operator !== 'isNotSet' &&
      operator !== 'isSet'
    ) {
      return false;
    }

    return checkCondition(condition, variableValue);
  }
  return true;
};

export const checkChoiceConditions = (
  data: UserDynamicValues,
  choice: FormattedInputChoice
) => {
  return choice.conditions.every((condition) =>
    checkBranchCondition(data, condition)
  );
};

export const filterChoices = (
  choices: FormattedInputChoice[],
  data: UserDynamicValues
): FormattedInputChoice[] => {
  return choices.filter((choice) => checkChoiceConditions(data, choice));
};

export function filterContentCards(
  featuredContent: ContentCardItem[],
  data: UserDynamicValues
): ContentCardItem[] {
  const featuredCards: ContentCardItem[] = [];

  featuredContent.map((card) => {
    if (card.conditions?.length === 0) {
      return featuredCards.push(card);
    }

    const check = card.conditions?.every((condition: Condition) => {
      return checkBranchCondition(data, condition);
    });

    if (check) {
      featuredCards.push(card);
    }
  });

  return featuredCards;
}
export function findDestination(
  destinations: Destination[],
  data: UserDynamicValues
): string | null {
  const found = destinations.find((item) => {
    // If there are no conditions then break out and pick this page
    if (item.conditions.length === 0) {
      return true;
    }

    return item.conditions.every((condition: Condition) => {
      return checkBranchCondition(data, condition);
    });
  });

  if (!found) {
    return null;
  }

  if (found.slug === 'index') {
    return '';
  }

  return found.slug;
}
